import { Platform } from 'react-native';

/**
 * Copy is store-policy constrained (specs/review-prompt AC-7): an invitation only —
 * never name a star count, never promise a reward, and the post-accept line stays
 * neutral because the OS review sheet can silently not appear (quota).
 */
export const REVIEW_PROMPT_COPY = {
  TITLE: 'Enjoying the game?',
  SUBTITLE_PREFIX: 'Your support helps us reach ',
  SUBTITLE_HIGHLIGHT: 'the top',
  SUBTITLE_SUFFIX: ' and build an even better experience!',
  GOLD_LINE: 'It only takes a second!',
  SUB_LINE:
    Platform.OS === 'ios'
      ? 'Tap to leave a quick rating on the App Store 💜'
      : 'Tap to leave a quick rating on Google Play 💜',
  CTA: 'Rate us',
  TOAST: 'Thanks for the support 💜',
  A11Y_CLOSE: 'Not now',
  A11Y_STAR: 'Rate the app',
} as const;

/** Design palette from Claude Design "Rating Modal.dc.html" — dark-committed, theme-independent. */
export const REVIEW_PROMPT_COLORS = {
  CARD_TOP: '#1c1230',
  CARD_BOTTOM: '#140d24',
  ACCENT: '#A78BFA',
  ACCENT_GLOW: 'rgba(167,139,250,0.25)',
  BADGE_BG: '#0F0A1A',
  GOLD: '#FCD34D',
  GOLD_DEEP: '#F59E0B',
  TITLE: '#EDE9FE',
  SUBTITLE: '#9CA3AF',
  BODY: '#D1D5DB',
  CLOSE_BG: '#374151',
  CLOSE_ICON: '#D1D5DB',
  CTA_TOP: '#B79CFC',
  CTA_MID: '#A78BFA',
  CTA_BOTTOM: '#8B6CF0',
  CTA_TEXT: '#111827',
  TOAST: '#6EE7B7',
  GLOW_INNER: '#5b34c4',
  GLOW_MID: '#442698',
  GROUND: '#241640',
} as const;

/** Floating-coin layout around the mascot (percent offsets within the stage). */
export interface ReviewPromptCoinSpec {
  left: `${number}%`;
  top: `${number}%`;
  size: number;
  durationMs: number;
  delayMs: number;
  rotateDeg: number;
}

export const REVIEW_PROMPT_COINS: readonly ReviewPromptCoinSpec[] = [
  { left: '2%', top: '34%', size: 44, durationMs: 4200, delayMs: 0, rotateDeg: -14 },
  { left: '12%', top: '68%', size: 34, durationMs: 3600, delayMs: 700, rotateDeg: 10 },
  { left: '16%', top: '8%', size: 28, durationMs: 4800, delayMs: 1300, rotateDeg: -6 },
  { left: '80%', top: '4%', size: 40, durationMs: 4400, delayMs: 300, rotateDeg: 12 },
  { left: '86%', top: '46%', size: 36, durationMs: 3800, delayMs: 1000, rotateDeg: -10 },
  { left: '74%', top: '72%', size: 30, durationMs: 4600, delayMs: 1800, rotateDeg: 8 },
];

/** Twinkle accents (✦) around the mascot stage. */
export interface ReviewPromptTwinkleSpec {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
  fontSize: number;
  color: string;
  durationMs: number;
  delayMs: number;
}

export const REVIEW_PROMPT_TWINKLES: readonly ReviewPromptTwinkleSpec[] = [
  { left: 24, top: 110, fontSize: 16, color: REVIEW_PROMPT_COLORS.GOLD, durationMs: 2400, delayMs: 0 },
  { right: 30, top: 50, fontSize: 13, color: REVIEW_PROMPT_COLORS.ACCENT, durationMs: 3000, delayMs: 800 },
  { right: 52, bottom: 36, fontSize: 12, color: REVIEW_PROMPT_COLORS.GOLD, durationMs: 2800, delayMs: 1400 },
];

export const REVIEW_PROMPT_STAR_COUNT = 5;

export const REVIEW_PROMPT_TIMING = {
  CARD_IN_MS: 500,
  MONKEY_IN_DELAY_MS: 150,
  STAR_IN_BASE_DELAY_MS: 350,
  STAR_IN_STAGGER_MS: 90,
  MONKEY_BOB_MS: 2600,
  CTA_PULSE_MS: 2400,
  /** How long the thanks toast stays up before the modal closes itself. */
  TOAST_CLOSE_DELAY_MS: 1600,
} as const;

export const REVIEW_PROMPT_LAYOUT = {
  MAX_CARD_WIDTH: 360,
  STAGE_HEIGHT: 230,
  MONKEY_SIZE: 200,
  STAR_SIZE: 44,
  STAR_HIT_SLOP: 6,
} as const;
